import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, Flag, Shield, Clock, CheckCircle, 
  XCircle, Eye, User, Newspaper, Building2, Loader2,
  Search, Filter, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const StrikesAdminPage = () => {
  const [reports, setReports] = useState([]);
  const [strikes, setStrikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reports'); // 'reports', 'strikes', 'claims'
  const [claims, setClaims] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showStrikeModal, setShowStrikeModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [strikeReason, setStrikeReason] = useState('');
  const [strikeSeverity, setStrikeSeverity] = useState('warning');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'reports') {
        const response = await api.get('/admin/strike-reports');
        setReports(response.data || []);
      } else if (activeTab === 'strikes') {
        const response = await api.get('/admin/strikes');
        setStrikes(response.data || []);
      } else if (activeTab === 'claims') {
        const response = await api.get('/admin/claims');
        setClaims(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Initialize with empty arrays if API fails
      if (activeTab === 'reports') setReports([]);
      else if (activeTab === 'strikes') setStrikes([]);
      else setClaims([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyStrike = async () => {
    if (!strikeReason.trim()) {
      toast.error('El motivo es requerido');
      return;
    }

    setProcessing(true);
    try {
      await api.post('/admin/strikes', {
        press_id: selectedReport.press_id,
        reason: strikeReason,
        severity: strikeSeverity,
        report_id: selectedReport.id
      });

      // Mark report as processed
      await api.put(`/admin/strike-reports/${selectedReport.id}`, {
        status: 'processed'
      });

      toast.success('Strike aplicado correctamente');
      setShowStrikeModal(false);
      setSelectedReport(null);
      setStrikeReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aplicar strike');
    } finally {
      setProcessing(false);
    }
  };

  const handleDismissReport = async (reportId) => {
    try {
      await api.put(`/admin/strike-reports/${reportId}`, {
        status: 'dismissed'
      });
      toast.success('Reporte descartado');
      fetchData();
    } catch (error) {
      toast.error('Error al descartar reporte');
    }
  };

  const [showClaimModal, setShowClaimModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [claimNotes, setClaimNotes] = useState('');
  const [createStrikeOnResolve, setCreateStrikeOnResolve] = useState(false);
  const [strikeTarget, setStrikeTarget] = useState('press');
  const [claimStrikeReason, setClaimStrikeReason] = useState('');

  const handleResolveClaimWithOptions = async () => {
    setProcessing(true);
    try {
      const updateData = {
        status: 'resolved',
        notes: claimNotes,
        create_strike: createStrikeOnResolve,
        strike_target: strikeTarget,
        strike_reason: claimStrikeReason || selectedClaim?.reason
      };
      
      const response = await api.put(`/admin/claims/${selectedClaim.id}`, updateData);
      
      if (response.data.strike_created) {
        const actionText = response.data.strike_action === 'warning' 
          ? 'Advertencia enviada' 
          : response.data.strike_action === 'suspension' 
          ? 'Usuario suspendido 7 días' 
          : 'Cuenta bloqueada';
        toast.success(`Reclamo resuelto. Strike #${response.data.total_strikes} aplicado. ${actionText}`);
      } else {
        toast.success('Reclamo resuelto');
      }
      
      setShowClaimModal(false);
      setSelectedClaim(null);
      setClaimNotes('');
      setCreateStrikeOnResolve(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al resolver reclamo');
    } finally {
      setProcessing(false);
    }
  };

  const handleResolveClaim = async (claimId, resolution) => {
    try {
      await api.put(`/admin/claims/${claimId}`, {
        status: resolution
      });
      toast.success(`Reclamo ${resolution === 'resolved' ? 'resuelto' : 'rechazado'}`);
      fetchData();
    } catch (error) {
      toast.error('Error al procesar reclamo');
    }
  };

  const getSeverityBadge = (severity) => {
    const configs = {
      warning: { color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30', label: 'Advertencia' },
      minor: { color: 'text-orange-400 bg-orange-500/10 border-orange-500/30', label: 'Menor' },
      major: { color: 'text-red-400 bg-red-500/10 border-red-500/30', label: 'Mayor' },
      critical: { color: 'text-red-600 bg-red-600/10 border-red-600/30', label: 'Crítico' }
    };
    const config = configs[severity] || configs.warning;
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30', label: 'Pendiente' },
      processed: { color: 'text-green-400 bg-green-500/10 border-green-500/30', label: 'Procesado' },
      dismissed: { color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/30', label: 'Descartado' },
      resolved: { color: 'text-green-400 bg-green-500/10 border-green-500/30', label: 'Resuelto' },
      rejected: { color: 'text-red-400 bg-red-500/10 border-red-500/30', label: 'Rechazado' }
    };
    const config = configs[status] || configs.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-white flex items-center gap-3">
                <Shield className="text-red-400" size={28} />
                Moderación y Strikes
              </h1>
              <p className="text-zinc-400 mt-1">Gestión de reportes, strikes y reclamos</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-zinc-800 pb-2 flex-wrap">
            <Button
              variant={activeTab === 'reports' ? 'default' : 'outline'}
              onClick={() => setActiveTab('reports')}
              className={activeTab === 'reports' ? 'bg-red-500 hover:bg-red-600' : ''}
            >
              <Flag size={16} className="mr-2" />
              Reportes ({reports.filter(r => r.status === 'pending').length})
            </Button>
            <Button
              variant={activeTab === 'strikes' ? 'default' : 'outline'}
              onClick={() => setActiveTab('strikes')}
              className={activeTab === 'strikes' ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : ''}
            >
              <AlertTriangle size={16} className="mr-2" />
              Strikes ({strikes.length})
            </Button>
            <Button
              variant={activeTab === 'claims' ? 'default' : 'outline'}
              onClick={() => setActiveTab('claims')}
              className={activeTab === 'claims' ? 'bg-blue-500 hover:bg-blue-600' : ''}
            >
              <XCircle size={16} className="mr-2" />
              Reclamos ({claims.filter(c => c.status === 'pending').length})
            </Button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-red-400" size={32} />
            </div>
          ) : (
            <>
              {/* Reports Tab */}
              {activeTab === 'reports' && (
                <div className="space-y-4">
                  {reports.length === 0 ? (
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                      <Flag className="mx-auto text-zinc-600 mb-4" size={48} />
                      <p className="text-zinc-400">No hay reportes pendientes</p>
                    </div>
                  ) : (
                    reports.map((report) => (
                      <motion.div
                        key={report.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-zinc-900/50 border rounded-xl p-5 ${
                          report.status === 'pending' ? 'border-red-500/30' : 'border-zinc-800'
                        }`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap mb-2">
                              <h3 className="text-white font-bold flex items-center gap-2">
                                <Newspaper size={16} className="text-blue-400" />
                                {report.press_name || 'Medio Reportado'}
                              </h3>
                              {getStatusBadge(report.status)}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-zinc-500 mb-2">
                              <Building2 size={14} />
                              <span>Reportado por: {report.reporter_name || 'Productor'}</span>
                            </div>
                            <div className="bg-zinc-800/50 rounded-lg p-3 mb-3">
                              <p className="text-zinc-300 text-sm">
                                <strong>Motivo:</strong> {report.reason}
                              </p>
                              {report.evidence && (
                                <p className="text-zinc-400 text-xs mt-1">
                                  <strong>Evidencia:</strong> {report.evidence}
                                </p>
                              )}
                            </div>
                            <p className="text-zinc-600 text-xs">
                              {new Date(report.created_at).toLocaleString()}
                            </p>
                          </div>
                          
                          {report.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-red-500 hover:bg-red-600"
                                onClick={() => {
                                  setSelectedReport(report);
                                  setShowStrikeModal(true);
                                }}
                              >
                                <AlertTriangle size={14} className="mr-1" />
                                Aplicar Strike
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDismissReport(report.id)}
                              >
                                Descartar
                              </Button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {/* Strikes Tab */}
              {activeTab === 'strikes' && (
                <div className="space-y-4">
                  {strikes.length === 0 ? (
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                      <AlertTriangle className="mx-auto text-zinc-600 mb-4" size={48} />
                      <p className="text-zinc-400">No hay strikes registrados</p>
                    </div>
                  ) : (
                    strikes.map((strike) => (
                      <motion.div
                        key={strike.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5"
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap mb-2">
                              <h3 className="text-white font-bold flex items-center gap-2">
                                <Newspaper size={16} className="text-blue-400" />
                                {strike.press_name || 'Medio'}
                              </h3>
                              {getSeverityBadge(strike.severity)}
                            </div>
                            <div className="bg-zinc-800/50 rounded-lg p-3 mb-3">
                              <p className="text-zinc-300 text-sm">{strike.reason}</p>
                            </div>
                            <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
                              <span>Aplicado por: {strike.applied_by || 'Admin'}</span>
                              <span>{new Date(strike.created_at).toLocaleString()}</span>
                              {strike.expires_at && (
                                <span className="text-yellow-400">
                                  Expira: {new Date(strike.expires_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {/* Claims Tab */}
              {activeTab === 'claims' && (
                <div className="space-y-4">
                  {claims.length === 0 ? (
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
                      <XCircle className="mx-auto text-zinc-600 mb-4" size={48} />
                      <p className="text-zinc-400">No hay reclamos pendientes</p>
                    </div>
                  ) : (
                    claims.map((claim) => (
                      <motion.div
                        key={claim.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-zinc-900/50 border rounded-xl p-5 ${
                          claim.status === 'pending' ? 'border-blue-500/30' : 'border-zinc-800'
                        }`}
                      >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap mb-2">
                              <h3 className="text-white font-bold">{claim.claimer_name}</h3>
                              <span className="text-zinc-500 text-sm">
                                ({claim.claimer_type === 'producer' ? 'Productora' : 'Prensa'})
                              </span>
                              {getStatusBadge(claim.status)}
                            </div>
                            <div className="flex flex-wrap gap-4 text-xs text-zinc-500 mb-2">
                              <span>Contrato: {claim.contract_id?.slice(-8).toUpperCase()}</span>
                              <span>Prensa: {claim.press_name}</span>
                              <span>Productor: {claim.producer_name}</span>
                            </div>
                            <div className="bg-zinc-800/50 rounded-lg p-3 mb-3">
                              <p className="text-zinc-300 text-sm">{claim.reason}</p>
                            </div>
                            <p className="text-zinc-600 text-xs">
                              {new Date(claim.created_at).toLocaleString()}
                            </p>
                          </div>
                          
                          {claim.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600"
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setShowClaimModal(true);
                                }}
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Resolver
                              </Button>
                              <Button
                                size="sm"
                                className="bg-red-500 hover:bg-red-600"
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setCreateStrikeOnResolve(true);
                                  setShowClaimModal(true);
                                }}
                              >
                                <AlertTriangle size={14} className="mr-1" />
                                + Strike
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                                onClick={() => handleResolveClaim(claim.id, 'rejected')}
                              >
                                Rechazar
                              </Button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Apply Strike Modal */}
        <Dialog open={showStrikeModal} onOpenChange={setShowStrikeModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="text-red-400" size={20} />
                Aplicar Strike
              </DialogTitle>
            </DialogHeader>
            
            {selectedReport && (
              <div className="space-y-4 py-4">
                <div className="bg-zinc-800/50 rounded-xl p-3">
                  <p className="text-zinc-400 text-sm">Medio: <strong className="text-white">{selectedReport.press_name}</strong></p>
                  <p className="text-zinc-500 text-xs mt-1">Motivo del reporte: {selectedReport.reason}</p>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Severidad del Strike</Label>
                  <Select value={strikeSeverity} onValueChange={setStrikeSeverity}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="warning">Advertencia</SelectItem>
                      <SelectItem value="minor">Menor</SelectItem>
                      <SelectItem value="major">Mayor</SelectItem>
                      <SelectItem value="critical">Crítico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Motivo del Strike</Label>
                  <Textarea
                    value={strikeReason}
                    onChange={(e) => setStrikeReason(e.target.value)}
                    placeholder="Describí el motivo del strike..."
                    className="bg-zinc-800 border-zinc-700 mt-1 min-h-[100px]"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowStrikeModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    className="flex-1 bg-red-500 hover:bg-red-600"
                    onClick={handleApplyStrike}
                    disabled={processing || !strikeReason.trim()}
                  >
                    {processing ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : (
                      <AlertTriangle size={16} className="mr-2" />
                    )}
                    Aplicar Strike
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Resolve Claim Modal */}
        <Dialog open={showClaimModal} onOpenChange={(open) => {
          setShowClaimModal(open);
          if (!open) {
            setSelectedClaim(null);
            setClaimNotes('');
            setCreateStrikeOnResolve(false);
            setClaimStrikeReason('');
          }
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Shield className="text-blue-400" size={20} />
                Resolver Reclamo
              </DialogTitle>
            </DialogHeader>
            
            {selectedClaim && (
              <div className="space-y-4 py-4">
                <div className="bg-zinc-800/50 rounded-xl p-3">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-400">Reclamante:</span>
                    <span className="text-white">{selectedClaim.claimer_name} ({selectedClaim.claimer_type === 'producer' ? 'Productora' : 'Prensa'})</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-400">Contra:</span>
                    <span className="text-white">{selectedClaim.claimer_type === 'producer' ? selectedClaim.press_name : selectedClaim.producer_name}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-zinc-400">Motivo: </span>
                    <span className="text-zinc-300">{selectedClaim.reason}</span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-zinc-400">Notas de resolución (opcional)</Label>
                  <Textarea
                    value={claimNotes}
                    onChange={(e) => setClaimNotes(e.target.value)}
                    placeholder="Comentarios sobre la resolución..."
                    className="bg-zinc-800 border-zinc-700 mt-1 min-h-[80px]"
                  />
                </div>
                
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="create_strike"
                      checked={createStrikeOnResolve}
                      onChange={(e) => setCreateStrikeOnResolve(e.target.checked)}
                      className="rounded bg-zinc-700 border-zinc-600 mt-1"
                    />
                    <div>
                      <Label htmlFor="create_strike" className="text-red-400 font-medium cursor-pointer">
                        Aplicar Strike al usuario
                      </Label>
                      <p className="text-zinc-500 text-xs mt-1">
                        1° strike = advertencia, 2° = suspensión 7 días, 3° = bloqueo de cuenta
                      </p>
                    </div>
                  </div>
                  
                  {createStrikeOnResolve && (
                    <div className="mt-4 space-y-3">
                      <div>
                        <Label className="text-zinc-400 text-sm">Aplicar strike a:</Label>
                        <Select value={strikeTarget} onValueChange={setStrikeTarget}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            <SelectItem value="press">
                              <span className="flex items-center gap-2">
                                <Newspaper size={14} />
                                Prensa: {selectedClaim.press_name}
                              </span>
                            </SelectItem>
                            <SelectItem value="producer">
                              <span className="flex items-center gap-2">
                                <Building2 size={14} />
                                Productora: {selectedClaim.producer_name}
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Motivo del strike (opcional)</Label>
                        <Input
                          value={claimStrikeReason}
                          onChange={(e) => setClaimStrikeReason(e.target.value)}
                          placeholder={selectedClaim.reason}
                          className="bg-zinc-800 border-zinc-700 mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowClaimModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    className={`flex-1 ${createStrikeOnResolve ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'}`}
                    onClick={handleResolveClaimWithOptions}
                    disabled={processing}
                  >
                    {processing ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : createStrikeOnResolve ? (
                      <AlertTriangle size={16} className="mr-2" />
                    ) : (
                      <CheckCircle size={16} className="mr-2" />
                    )}
                    {createStrikeOnResolve ? 'Resolver + Strike' : 'Resolver'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default StrikesAdminPage;
